// RealWatchPage.tsx — watching a real video post. v2 (1.27.50) after the owner's concept
// docs/References/Images/video_watch_page_v2.webp:
//
//   TruXSocial's own player (chapters, speed, quality, repeat, sleep timer, PiP, theater…)
//   title · views · age
//   creator · Follow · bell (post notifications)
//   Like | Dislike · Share · Quote · Save (folder) · Clip · ⋯
//   description (Show more) · chapters · the post's REAL comments
//   Up next: For you / From <creator> / Related + Autoplay → the shell's right column
//
// Owner rulings: Like / Dislike and no Cherish here; no comment button in the action row
// (comments are right below). A video IS a post, so reactions, the Under Review label,
// hiding, reporting and the comments are the post's own — nothing here is a second system.
// Spread (TruXSocial's re-broadcast) sits in the Share dialog.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { Button, MetaRow, useToast } from '@/ui';
import { quietScrollbar } from '@/theme/scrollbar';
import { Icon } from '@/icons';
import { RailGroup } from '@/app/shell/RightRail';
import { usePost, usePostStatus, useRequireAuth, useSession, useViewport } from '@/hooks';
import { useWriteGate } from '@/hooks/writeGate';
import { postsService } from '@/core/services/postsService';
import type { UpNextMode } from '@/core/services/videoFeedService';
import { useQuoteDraftStore } from '@/core/stores/quoteDraftStore';
import { useComposeSheetStore } from '@/core/stores/composeSheetStore';
import { PostStatus } from '@/components/PostCard/PostStatus';
import { EditPostDialog } from '@/components/PostCard/EditPostDialog';
import { DeletePostDialog } from '@/components/PostCard/DeletePostDialog';
import { SharePostDialog } from '@/components/PostCard/SharePostDialog';
import { AudienceBadge } from '@/components/PostCard/AudienceBadge';
import { useOwnPostActions } from '@/components/PostCard/useOwnPostActions';
import { ReportModal } from '@/components/ReportModal';
import { AppealModal } from '@/components/AppealModal';
import { CommentsSection } from '@/features/post-detail/CommentsSection';
import { DiscussionToolsProvider } from '@/features/discussion-tools/DiscussionToolsProvider';
import type { Post } from '@/core/models/post';
import { useVideoTheater } from '../useVideoTheater';
import { useWatchRail } from '../useWatchRail';
import { CommentsArea, DetailsArea, PlayerArea, RailArea, WatchGrid } from '../watchGrid';
import { formatViews } from '../meta';
import { VideoPlayer, type VideoPlayerHandle } from '../player/VideoPlayer';
import { chapterAt, formatClock, parseChapters, parseStartParam } from '../player/chapters';
import { videoDescription, videoTitle } from './realItems';
import { useUpNext } from './useRealVideos';
import { ActionPill, ActionRow } from './watch/actionStyles';
import { CreatorRow } from './watch/CreatorRow';
import { ReactionPair } from './watch/ReactionPair';
import { SaveMenu } from './watch/SaveMenu';
import { UpNextPanel } from './watch/UpNextPanel';
import { VideoMoreMenu } from './watch/VideoMoreMenu';
import { useWatchProgress } from './watch/useWatchProgress';
import { useVideoQueue } from '../queue/videoQueue';
import { pickTrack, useCaptionTracks, useCues } from '../captions/useCaptions';
import { cueAt } from '../captions/vtt';
import { usePlayerPrefs } from '../player/playerPrefs';
import { TranscriptPanel } from './watch/TranscriptPanel';
import { useNowPlaying } from './watch/nowPlaying';
import { ClipDialog } from './watch/ClipDialog';
import { ClipWatch } from './watch/ClipWatch';

const Title = styled.h1`
  margin: 16px 0 4px;
  font-family: ${({ theme }) => theme.font.display};
  font-size: ${({ theme }) => theme.fontSize.lg};
  font-weight: 700;
  line-height: 1.25;
  color: ${({ theme }) => theme.color.textPrimary};
  overflow-wrap: anywhere;
`;

const Meta = styled.p`
  margin: 0 0 14px;
  font: 400 ${({ theme }) => theme.fontSize.sm} ${({ theme }) => theme.font.body};
  color: ${({ theme }) => theme.color.textMuted};
`;

const ResumeNote = styled.p`
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 10px 0 0;
  font: 500 ${({ theme }) => theme.fontSize.sm} ${({ theme }) => theme.font.body};
  color: ${({ theme }) => theme.color.textMuted};
  font-variant-numeric: tabular-nums;
  button {
    padding: 0;
    border: 0;
    background: none;
    color: ${({ theme }) => theme.color.accentCyan};
    font: inherit;
    font-weight: 600;
    cursor: pointer;
  }
`;

const Actions = styled(ActionRow)`
  margin-top: 14px;
`;

const MoreSlot = styled.span`
  margin-left: auto;
`;

const DescriptionBox = styled.div`
  margin-top: 14px;
  padding: 12px 14px;
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => (theme.cardsGlass ? theme.color.glassBg : theme.color.surface)};
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
`;

const DescriptionText = styled.p<{ $clamped: boolean }>`
  margin: 0;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  line-height: 1.55;
  color: ${({ theme }) => theme.color.textPrimary};
  ${({ $clamped }) =>
    $clamped &&
    css`
      display: -webkit-box;
      -webkit-line-clamp: 3;
      -webkit-box-orient: vertical;
      overflow: hidden;
    `}
`;

const MoreToggle = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-top: 6px;
  padding: 0;
  border: 0;
  background: none;
  cursor: pointer;
  font: 600 ${({ theme }) => theme.fontSize.sm} ${({ theme }) => theme.font.body};
  color: ${({ theme }) => theme.color.accentCyan};
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
    outline-offset: 2px;
  }
`;

const ChaptersBox = styled.section`
  margin-top: 16px;
  scroll-margin-top: 80px;
  h2 {
    margin: 0 0 8px;
    font: 700 ${({ theme }) => theme.fontSize.md} ${({ theme }) => theme.font.display};
    color: ${({ theme }) => theme.color.textPrimary};
  }
  > ol {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    gap: 8px;
    overflow-x: auto;
    padding-bottom: 4px;
    ${quietScrollbar}
  }
`;

const ChapterChip = styled.button<{ $on: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 150px;
  max-width: 220px;
  padding: 8px 12px;
  border-radius: 10px;
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  background: ${({ theme, $on }) => ($on ? theme.color.surfaceRaised : theme.color.surface)};
  color: ${({ theme }) => theme.color.textPrimary};
  text-align: left;
  cursor: pointer;
  font: 600 ${({ theme }) => theme.fontSize.sm} ${({ theme }) => theme.font.body};
  &:hover {
    background: ${({ theme }) => theme.color.surfaceRaised};
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
    outline-offset: 2px;
  }
  time {
    color: ${({ theme }) => theme.color.accentCyan};
    font-variant-numeric: tabular-nums;
    font-size: ${({ theme }) => theme.fontSize.xs};
  }
  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

// Concept "Chapters | Transcript": text tabs, the chosen one underlined.
const SectionTabs = styled.div`
  display: flex;
  gap: 18px;
  margin-bottom: 10px;
  border-bottom: 1px solid ${({ theme }) => theme.color.glassBorder};
`;

const SectionTab = styled.button<{ $on: boolean }>`
  padding: 6px 2px 8px;
  margin-bottom: -1px;
  border: 0;
  border-bottom: 2px solid ${({ theme, $on }) => ($on ? theme.color.accentCyan : 'transparent')};
  background: none;
  color: ${({ theme, $on }) => ($on ? theme.color.accentCyan : theme.color.textMuted)};
  font: 600 ${({ theme }) => theme.fontSize.sm} ${({ theme }) => theme.font.body};
  cursor: pointer;
  &:hover {
    color: ${({ theme }) => theme.color.textPrimary};
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
    outline-offset: 2px;
  }
`;

const UnlistedNote = styled.p`
  margin: 0;
  font: 500 ${({ theme }) => theme.fontSize.xs} ${({ theme }) => theme.font.body};
  color: ${({ theme }) => theme.color.textMuted};
`;

const ShareExtras = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  label {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font: 500 ${({ theme }) => theme.fontSize.sm} ${({ theme }) => theme.font.body};
    color: ${({ theme }) => theme.color.textPrimary};
    font-variant-numeric: tabular-nums;
  }
  input {
    accent-color: ${({ theme }) => theme.color.accentCyan};
  }
`;

const Center = styled.div`
  padding: 40px 16px;
  text-align: center;
  font-family: ${({ theme }) => theme.font.body};
  color: ${({ theme }) => theme.color.textMuted};
`;

/** Loads the post (store first, then the server), then hands a REAL post to the page — the
 *  post-card hooks below all assume one exists. */
export function RealWatchPage({ postId }: { postId: string }) {
  const { post } = usePost(postId);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    let live = true;
    void postsService.getPost(postId).then((found) => {
      // No post, or a post with no video (and not a clip): nothing to watch here.
      if (live && !found?.video && !found?.clip) setMissing(true);
    });
    return () => {
      live = false;
    };
  }, [postId]);

  if (post?.clip) return <ClipWatch key={post.id} post={post} />;
  if (!post || !post.video) {
    if (missing || (post && !post.video)) {
      return (
        <Center>
          <p>This video couldn&apos;t be found.</p>
          <Link to="/videos">Back to Videos</Link>
        </Center>
      );
    }
    return <Center role="status">Loading video…</Center>;
  }
  return <WatchContent key={post.id} post={post} />;
}

function WatchContent({ post }: { post: Post }) {
  const { currentUser, isAuthenticated, updateProfile } = useSession();
  const unlisted = post.audience?.type === 'unlisted';
  const requireAuth = useRequireAuth();
  const gate = useWriteGate();
  const toast = useToast();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const status = usePostStatus(post.id);
  const own = useOwnPostActions(post);
  const { spread } = usePost(post.id);
  const setPendingQuote = useQuoteDraftStore((s) => s.setPendingQuote);
  const openComposeSheet = useComposeSheetStore((s) => s.openSheet);
  const [reporting, setReporting] = useState(false);
  const [appealingCaseId, setAppealingCaseId] = useState<string | null>(null);
  const [sharing, setSharing] = useState(false);
  // Where the player was when Clip opened — the clip starts there.
  const [clipFrom, setClipFrom] = useState<number | null>(null);
  const [shareAtTime, setShareAtTime] = useState(false);
  // Where the player was when Share opened — the "Start at" link points there.
  const [shareFrom, setShareFrom] = useState(0);
  const [expanded, setExpanded] = useState(false);
  const [views, setViews] = useState<number | null>(null);
  const playerRef = useRef<VideoPlayerHandle>(null);
  const [startAt] = useState(() => parseStartParam(params.get('t')));

  const theater = useVideoTheater((s) => s.theater);
  const toggleTheater = useVideoTheater((s) => s.toggle);
  const isDesktop = useViewport() === 'desktop';
  const effectiveTheater = theater && isDesktop;

  // Autoplay: the member's setting; a per-visit switch when signed out.
  const [guestAutoplay, setGuestAutoplay] = useState(false);
  const autoplay = isAuthenticated ? Boolean(currentUser?.autoplayNext) : guestAutoplay;
  const [mode, setMode] = useState<UpNextMode>('for_you');
  // Fetched here, not in the rail: autoplay plays the first video of the tab that is showing,
  // wherever the rail sits (and theater mode used to unmount the rail).
  const upNext = useUpNext(post.id, mode);
  // The queue plays before Up next; watching a queued video takes it off.
  const queueItems = useVideoQueue((s) => s.items);
  const removeQueued = useVideoQueue((s) => s.remove);
  const clearQueue = useVideoQueue((s) => s.clear);
  useEffect(() => removeQueued(post.id), [post.id, removeQueued]);
  const queue = useMemo(() => queueItems.filter((q) => q.id !== post.id), [queueItems, post.id]);

  const changeAutoplay = useCallback(
    (next: boolean) => {
      if (!isAuthenticated) {
        setGuestAutoplay(next);
        return;
      }
      void updateProfile({ autoplayNext: next }).then((ok) => {
        if (!ok) toast('Something went wrong');
      });
    },
    [isAuthenticated, updateProfile, toast],
  );

  const rail = useMemo(
    () => (
      <UpNextPanel
        creatorName={post.name}
        mode={mode}
        onMode={setMode}
        posts={upNext}
        autoplay={autoplay}
        onAutoplay={changeAutoplay}
        queue={queue}
        onRemoveQueued={removeQueued}
        onClearQueue={clearQueue}
      />
    ),
    [post.name, mode, upNext, autoplay, changeAutoplay, queue, removeQueued, clearQueue],
  );
  const shellRail = useMemo(() => <RailGroup>{rail}</RailGroup>, [rail]);
  const railPlace = useWatchRail(shellRail, theater);

  const video = post.video!;
  const title = videoTitle(post);
  const description = videoDescription(post);
  const durationSec = video.durationMs / 1000;
  const chapters = useMemo(() => parseChapters(post.body, durationSec), [post.body, durationSec]);
  // The chapter playing now — state only changes when the chapter does, not every tick.
  const [chapterStart, setChapterStart] = useState<number | null>(null);
  const progress = useWatchProgress(post, isAuthenticated);
  const saveProgressAt = progress.onTime;
  // A link's ?t= wins over your saved place.
  const resumeFrom = startAt === undefined ? progress.resumeFrom : undefined;
  const [resumeNote, setResumeNote] = useState(resumeFrom !== undefined);
  // ── Transcript (1.27.60): the caption tracks the player uses, shown as text ───
  const captionTracks = useCaptionTracks(video.id);
  const hasTranscript = Boolean(captionTracks?.length);
  const preferredLang = usePlayerPrefs((s) => s.prefs.captionLang);
  const [transcriptLang, setTranscriptLang] = useState<string | null>(null);
  const transcriptTrack = pickTrack(captionTracks, transcriptLang ?? preferredLang);
  const [section, setSection] = useState<'chapters' | 'transcript'>('chapters');
  const shownSection = !chapters.length ? 'transcript' : !hasTranscript ? 'chapters' : section;
  const transcriptCues = useCues(
    video.id,
    hasTranscript && shownSection === 'transcript' ? (transcriptTrack?.language ?? null) : null,
  );
  const activeLine = useNowPlaying((s) =>
    transcriptCues ? (cueAt(transcriptCues, s.t)?.start ?? null) : null,
  );
  const sectionRef = useRef<HTMLElement>(null);
  const showTranscript = useCallback(() => {
    setSection('transcript');
    sectionRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
  }, []);
  useEffect(() => useNowPlaying.getState().setT(startAt ?? 0), [post.id, startAt]);

  const onTime = useCallback(
    (t: number) => {
      saveProgressAt(t);
      useNowPlaying.getState().setT(t);
      if (!chapters.length) return;
      const c = chapterAt(chapters, t);
      setChapterStart((prev) => (c && c.start !== prev ? c.start : prev));
    },
    [chapters, saveProgressAt],
  );
  const shownViews = views ?? video.views ?? 0;
  const isOwn = own.isOwn;
  const next = queue[0] ?? upNext?.[0];
  const nowAt = () => playerRef.current?.currentTime() ?? 0;

  function quote() {
    if (!requireAuth('quote')) return;
    setPendingQuote(post);
    openComposeSheet();
  }

  const shareUrl = `${window.location.origin}/videos/${post.id}${
    shareAtTime ? `?t=${Math.floor(shareFrom)}` : ''
  }`;

  return (
    <DiscussionToolsProvider key={`${post.id}:${currentUser?.id ?? 'guest'}`} post={post}>
      <WatchGrid $rail={railPlace} data-testid="real-watch-page">
        <PlayerArea>
          <VideoPlayer
            ref={playerRef}
            video={video}
            title={title}
            chapters={chapters}
            startAt={startAt ?? resumeFrom}
            autoPlay={startAt !== undefined}
            theater={effectiveTheater}
            onToggleTheater={isDesktop ? toggleTheater : undefined}
            autoplayNext={autoplay}
            onAutoplayNextChange={changeAutoplay}
            onViewCounted={setViews}
            onTime={onTime}
            pageShortcuts
            onEnded={() => {
              progress.onEnded();
              if (autoplay && next) navigate(`/videos/${next.id}`);
            }}
          />
          {resumeNote && resumeFrom !== undefined && (
            <ResumeNote role="status">
              Picking up where you left off, at {formatClock(resumeFrom)}.
              <button
                type="button"
                onClick={() => {
                  playerRef.current?.seekTo(0);
                  setResumeNote(false);
                }}
              >
                Start over
              </button>
            </ResumeNote>
          )}
        </PlayerArea>
        <DetailsArea>
          <Title>{title}</Title>
          <Meta>
            <MetaRow>
              <span>{formatViews(shownViews)}</span>
              <span>{post.time}</span>
              {post.audience && post.audience.type !== 'everyone' && (
                <AudienceBadge audience={post.audience} />
              )}
            </MetaRow>
          </Meta>
          <CreatorRow post={post} isOwn={isOwn} />

          <Actions>
            <ReactionPair post={post} />
            <ActionPill
              type="button"
              onClick={() => {
                if (!gate.allow('share')) return;
                setShareFrom(nowAt());
                setSharing(true);
              }}
            >
              <Icon name="share" size={18} />
              Share
            </ActionPill>
            <ActionPill type="button" onClick={quote}>
              <Icon name="quote" size={18} />
              Quote
            </ActionPill>
            <SaveMenu post={post} />
            {post.video?.allowClips !== false && (
              <ActionPill
                type="button"
                onClick={() => {
                  if (requireAuth('clip videos')) setClipFrom(nowAt());
                }}
              >
                <Icon name="scissors" size={18} />
                Clip
              </ActionPill>
            )}
            <MoreSlot>
              <VideoMoreMenu
                post={post}
                title={title}
                isOwn={isOwn}
                nextHref={next ? `/videos/${next.id}` : '/videos'}
                currentTime={nowAt}
                onReport={() => {
                  if (requireAuth('report')) setReporting(true);
                }}
                onEdit={own.openEdit}
                onDelete={own.openDelete}
                onShowTranscript={hasTranscript ? showTranscript : undefined}
              />
            </MoreSlot>
          </Actions>

          <PostStatus
            status={status}
            onAppeal={(caseId) => {
              if (requireAuth('appeal')) setAppealingCaseId(caseId);
            }}
          />

          {description && (
            <DescriptionBox>
              <DescriptionText $clamped={!expanded}>{description}</DescriptionText>
              {description.length > 180 && (
                <MoreToggle type="button" onClick={() => setExpanded((v) => !v)}>
                  {expanded ? 'Show less' : 'Show more'}
                  <Icon name={expanded ? 'chevron-up' : 'chevron-down'} size={16} />
                </MoreToggle>
              )}
            </DescriptionBox>
          )}

          {(chapters.length > 0 || hasTranscript) && (
            <ChaptersBox
              ref={sectionRef}
              aria-label={shownSection === 'chapters' ? 'Chapters' : 'Transcript'}
            >
              {chapters.length > 0 && hasTranscript ? (
                <SectionTabs role="tablist" aria-label="Chapters and transcript">
                  <SectionTab
                    type="button"
                    role="tab"
                    aria-selected={shownSection === 'chapters'}
                    $on={shownSection === 'chapters'}
                    onClick={() => setSection('chapters')}
                  >
                    Chapters
                  </SectionTab>
                  <SectionTab
                    type="button"
                    role="tab"
                    aria-selected={shownSection === 'transcript'}
                    $on={shownSection === 'transcript'}
                    onClick={() => setSection('transcript')}
                  >
                    Transcript
                  </SectionTab>
                </SectionTabs>
              ) : (
                <h2>{shownSection === 'chapters' ? 'Chapters' : 'Transcript'}</h2>
              )}
              {shownSection === 'transcript' && captionTracks && transcriptTrack ? (
                <TranscriptPanel
                  tracks={captionTracks}
                  language={transcriptTrack.language}
                  onLanguage={setTranscriptLang}
                  cues={transcriptCues}
                  activeStart={activeLine}
                  onSeek={(s) => {
                    playerRef.current?.seekTo(s);
                    playerRef.current?.play();
                  }}
                />
              ) : (
                <ol>
                  {chapters.map((c) => (
                    <li key={c.start}>
                      <ChapterChip
                        type="button"
                        $on={chapterStart === c.start}
                        aria-current={chapterStart === c.start}
                        onClick={() => {
                          playerRef.current?.seekTo(c.start);
                          playerRef.current?.play();
                        }}
                      >
                        <time>{formatClock(c.start)}</time>
                        <span>{c.title}</span>
                      </ChapterChip>
                    </li>
                  ))}
                </ol>
              )}
            </ChaptersBox>
          )}
        </DetailsArea>

        {railPlace !== 'shell' && <RailArea>{rail}</RailArea>}

        <CommentsArea>
          <CommentsSection key={`${post.id}:${currentUser?.id ?? 'guest'}`} postId={post.id} />
        </CommentsArea>
      </WatchGrid>

      {sharing && (
        <SharePostDialog
          post={post}
          url={shareUrl}
          onClose={() => setSharing(false)}
          extra={
            <ShareExtras>
              <label>
                <input
                  type="checkbox"
                  checked={shareAtTime}
                  onChange={(e) => setShareAtTime(e.target.checked)}
                />
                Start at {formatClock(shareFrom)}
              </label>
              {unlisted ? (
                <UnlistedNote>
                  Unlisted: only people you send this link to can watch it.
                </UnlistedNote>
              ) : (
                <Button
                  variant="secondary"
                  onClick={() => {
                    if (!requireAuth('spread')) return;
                    void spread().then(() => toast('Spread to your followers'));
                    setSharing(false);
                  }}
                >
                  <Icon name="spread-it" size={16} /> Spread to followers
                </Button>
              )}
            </ShareExtras>
          }
        />
      )}
      {clipFrom !== null && (
        <ClipDialog
          post={post}
          title={title}
          fromSec={clipFrom}
          onClose={() => setClipFrom(null)}
        />
      )}
      {reporting && (
        <ReportModal target={{ kind: 'post', post }} onClose={() => setReporting(false)} />
      )}
      {appealingCaseId && (
        <AppealModal caseId={appealingCaseId} onClose={() => setAppealingCaseId(null)} />
      )}
      {own.editing && (
        <EditPostDialog
          post={post}
          onClose={own.closeEdit}
          onSave={own.saveEdit}
          editWindowAdvisory={own.editWindowAdvisory}
        />
      )}
      <DeletePostDialog
        open={own.deleting}
        onCancel={own.closeDelete}
        onConfirm={() => void own.confirmDelete()}
      />
    </DiscussionToolsProvider>
  );
}
